import React, { useContext, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import BrandRedirect from '../components/BrandRedirect/BrandRedirect'
import { brandLoginUrl } from '../common/brand-login'
import { getIntercomTrialAttributes } from '../common/intercom-trial-attributes'
import { getPlanLabel } from '../common/plan-label'
import { trialEligibility } from '../services/api/subscription'
import Intercom, { update } from '@intercom/messenger-js-sdk'
import { identify } from '@sailfish-ai/recorder'
import { AuthContext, AuthStatusContext } from '../context/firebase-context'
import { getWorkspaceInviteTokenFromPath, persistPendingWorkspaceInvite } from '../common/workspace-invitation'
import Preloader from '../components/Preloader/Preloader'
import AuthFailureScreen from '../components/AuthFailureScreen/AuthFailureScreen'
import VerifyEmailBanner from '../components/core/VerifyEmailBanner'
import UserBlockedBanner from '../components/core/UserBlockedBanner'

const INTERCOM_ID = process.env.REACT_APP_INTERCOM_ID
const SHOW_BANNER_AFTER = new Date(2024, 9, 14)

export default function RequireAuth ({ children }) {
  const user = useContext(AuthContext)
  const { hydrating, hydrateFailed } = useContext(AuthStatusContext)
  const location = useLocation()

  // Identity is reported once per account. `user` is a fresh object after every
  // profile or payment update, so keying the effect on it alone would re-boot
  // Intercom on each one.
  const identifiedUuid = useRef(null)

  // Both effects sit above the early returns on purpose. They used to live
  // below them, which meant the number of hooks this component ran depended on
  // whether a user was present — fine while a signed-out render always
  // redirected away, but not once `hydrating` (or a retry after
  // `hydrateFailed`) can turn the same mount from "no user" into "user" in
  // place.
  useEffect(() => {
    if (!user || identifiedUuid.current === user.userUuid) return
    identifiedUuid.current = user.userUuid

    const credits = (user.credits?.available_credits || 0) + (user.credits?.purchased_credits || 0)
    let remainingRequests = false
    if (user?.remainingRequests) {
      try {
        const aiRequests = user.remainingRequests.find(r => r.name === 'AI Requests')
        if (aiRequests) {
          if (aiRequests.limit === 'Unlimited') {
            remainingRequests = 'Unlimited'
          } else {
            remainingRequests = aiRequests.limit - parseInt(aiRequests.count)
          }
        }
      } catch (error) {
        console.warn('cant process requests left', error)
      }
    }
    if (INTERCOM_ID) {
      Intercom({
        // Hidden everywhere. The floating bubble sat next to Orson on the
        // dashboard and read as an in-app agent rather than support. Booting
        // is unchanged, so identity and every custom attribute below still
        // reach Intercom — only the launcher is gone. `showNewMessage`, which
        // NavigatorAgent and the Help buttons call, still opens the messenger.
        hide_default_launcher: true,
        app_id: INTERCOM_ID,
        avatar: user.profile?.profile_picture || user.photoURL,
        user_id: user.userUuid, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
        name: user.profile?.profile_name || user.displayName, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
        email: user.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
        created_at: Math.floor(parseInt(user.createdAt) / 1000),
        'User Subscription': user.subscriptionPlan || 'free',
        'Email Verified': user.emailVerified,
        'User Role': user.profile?.role,
        'Available Credits': credits,
        'AI Requests Left': remainingRequests,
        'Total Scripts': user?.totalScripts || 0,
        'Profile Completeness': user?.profileCompleteness || 0,
        'Subscription Expires': user?.subscriptionData ? new Date(user.subscriptionData.expiration_at) : null,
        ...getIntercomTrialAttributes(user)
      })
      // Needs Stripe history, so it follows the boot rather than delaying it;
      // inside the chain so no failure can reach the render.
      Promise.resolve()
        .then(() => trialEligibility())
        .then((result) => update({
          'Previously Subscribed': Boolean(result?.previouslySubscribed),
          'Previous Plan': getPlanLabel(result?.previousPlan)?.label || null
        }))
        .catch(() => {})
    }
    identify(user.email, {
      userId: user.userUuid,
      name: user.profile?.profile_name || user.displayName,
      createdAt: new Date(user.createdAt),
      userSubscription: user.subscriptionPlan || 'free',
      availableCredits: credits
    })
  }, [user])

  useEffect(() => {
    if (!user) return
    try {
      if (INTERCOM_ID) {
        // Kept after the launcher went away: this is also what pings Intercom
        // on navigation (`last_request_at`), so dropping it would cost session
        // data. The trial attributes ride along so 'Trial Day' stays current
        // through long sessions.
        update({
          hide_default_launcher: true,
          ...getIntercomTrialAttributes(user)
        })
      }
    } catch (error) {
      console.warn(error)
    }
  }, [user, location.pathname])

  // Signed in, but the profile and subscription calls behind `AuthContext` are
  // still in flight. That is not "signed out": redirecting here is what threw a
  // visitor arriving from the brand handoff onto `/` mid-sign-in and cost them
  // a second full boot of the app.
  if (!user && hydrating) {
    return <Preloader />
  }

  // Firebase is signed in but the account failed to load. Redirecting to `/`
  // would bounce straight back here, forever (NOLAN-STUDIO-22W).
  if (!user && hydrateFailed) {
    return <AuthFailureScreen returnTo={`${location.pathname}${location.search}`} />
  }

  if (!user) {
    persistPendingWorkspaceInvite(location.pathname)
    if (getWorkspaceInviteTokenFromPath(location.pathname)) {
      return <BrandRedirect to={brandLoginUrl(location.pathname)} />
    }
    // `r` survives the brand login, so a deep link (a billing email's
    // /profile?billing=portal) lands back on that page with its query.
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/?r=${returnTo}`} state={{ from: location }} replace />
  }

  const registered = new Date(parseInt(user.createdAt))
  if (registered > SHOW_BANNER_AFTER && !user.emailVerified) {
    return <VerifyEmailBanner user={user} />
  }
  if (user.userBlocked) {
    return <UserBlockedBanner />
  }

  return (
    <>
      {!user.emailVerified && <VerifyEmailBanner user={user} />}
      {children}
    </>
  )
}
